import React, { useState } from 'react';
import apiClient from '../api';
import '../styles/github.css';
import MemoLogo from './MemoLogo';

function LoginModal({ onClose, onLoginSuccess, theme }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);

  // 发送验证码函数
  const sendVerificationCode = async () => {
    if (!email) {
      setRegisterError('请输入邮箱');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRegisterError('邮箱格式不正确');
      return;
    }
    
    setRegisterError('');
    setLoading(true);
    
    try {
      const response = await apiClient.post('/send_verification_code', {
        email,
        purpose: 'register'
      });
      
      setCodeSent(true);
      setCountdown(60);
      
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCodeSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      alert('验证码已发送到邮箱，请查收');
      if (response.data.debug_code) {
        console.log('测试用验证码:', response.data.debug_code);
      }
    } catch (err) {
      console.error('发送验证码失败:', err);
      setRegisterError(err.response?.data?.error || '发送验证码失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 忘记密码：发送重置邮件
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
  
    try {
      const response = await apiClient.post('/forgot_password', { email: forgotEmail });
    
      if (response.data && response.data.success) {
        alert(response.data.message || '重置链接已发送到您的邮箱');
      
        if (response.data.debug && response.data.debug.link) {
          console.log('调试链接:', response.data.debug.link);
        }
      
        setShowForgotPassword(false);
        setForgotEmail('');
      } else {
        setLoginError(response.data?.error || '未知错误');
      }
    } catch (err) {
      console.error('发送重置邮件失败:', err);
      if (err.message && err.message.includes('邮件发送失败')) {
        alert('重置链接已生成，但邮件发送失败。请检查您的网络或联系管理员。');
        setShowForgotPassword(false);
        setForgotEmail('');
      } else {
        setLoginError(err.message || '发送重置邮件失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 清除之前的所有错误
    setLoginError('');
    setRegisterError('');
    
    // 表单验证
    if (isRegister) {
      if (password !== confirmPassword) {
        setRegisterError('两次输入的密码不一致');
        return;
      }
      
      if (!email || !verificationCode) {
        setRegisterError('邮箱和验证码都是必填项');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (isRegister) {
        // 注册请求
        const response = await apiClient.post('/register', { 
          username, 
          password,
          email,
          verification_code: verificationCode
        });
        alert('注册成功！请登录。');
        setIsRegister(false);
        setEmail('');
        setVerificationCode('');
        setConfirmPassword('');
        setRegisterError('');
      } else {
        // 登录请求
        const response = await apiClient.post('/login', { username, password });
        const { token, user } = response.data;
        onLoginSuccess(token, user);
        onClose();
      }
    } catch (err) {
      console.error('请求失败:', err);
      const errorMessage = err.response?.data?.error || (isRegister ? '注册失败' : '登录失败，请检查用户名和密码');
      
      if (isRegister) {
        setRegisterError(errorMessage);
      } else {
        setLoginError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'login',
      label: '登录',
      active: !isRegister,
      onClick: () => {
        setIsRegister(false);
        setLoginError('');
        setRegisterError('');
      }
    },
    {
      id: 'register',
      label: '注册',
      active: isRegister,
      onClick: () => {
        setIsRegister(true);
        setLoginError('');
        setRegisterError('');
      }
    }
  ];

  const styles = {
    modalContainer: {
      padding: '40px',
      width: '420px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '28px',
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: 'var(--color-text-primary)',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: 'var(--color-text-secondary)',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '28px',
      padding: '4px',
      background: 'var(--color-bg-secondary)',
      borderRadius: '8px',
    },
    tab: {
      flex: 1,
      padding: '12px',
      textAlign: 'center',
      background: 'transparent',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    tabActive: {
      background: 'var(--color-bg-primary)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      color: 'var(--color-text-primary)',
    },
    tabInactive: {
      color: 'var(--color-text-secondary)',
    },
    errorAlert: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '14px 16px',
      backgroundColor: 'rgba(203, 36, 49, 0.1)',
      border: '1px solid rgba(203, 36, 49, 0.2)',
      borderRadius: '8px',
      color: 'var(--color-btn-danger)',
      marginBottom: '20px',
      fontSize: '14px',
    },
    errorIcon: {
      width: '16px',
      height: '16px',
      flexShrink: '0',
    },
    form: {
      marginBottom: '24px',
    },
    formGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: 'var(--color-text-primary)',
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '15px',
      lineHeight: '20px',
      color: 'var(--color-text-primary)',
      backgroundColor: 'var(--color-bg-primary)',
      border: '1px solid var(--color-border-primary)',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
    },
    inputHint: {
      display: 'block',
      fontSize: '12px',
      color: 'var(--color-text-tertiary)',
      marginTop: '6px',
    },
    codeInputGroup: {
      display: 'flex',
      gap: '8px',
    },
    codeInput: {
      flex: '1',
    },
    sendCodeButton: {
      whiteSpace: 'nowrap',
      minWidth: '120px',
      padding: '12px',
    },
    submitButton: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      fontWeight: '600',
      marginTop: '24px',
      borderRadius: '8px',
    },
    footerText: {
      fontSize: '14px',
      color: 'var(--color-text-secondary)',
      textAlign: 'center',
      marginTop: '16px',
    },
    footerLink: {
      color: 'var(--color-accent)',
      textDecoration: 'none',
      fontWeight: '600',
      cursor: 'pointer',
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      color: 'var(--color-text-tertiary)',
      fontSize: '18px',
      userSelect: 'none',
      zIndex: 2,
    }
  };

  // 忘记密码界面
  if (showForgotPassword) {
    return (
      <div style={styles.modalContainer}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <svg style={{verticalAlign: 'middle', marginRight: '8px'}} width="24" height="24" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M1.5 1.75a.25.25 0 01.25-.25h12.5a.25.25 0 01.25.25v12.5a.25.25 0 01-.25.25H1.75a.25.25 0 01-.25-.25V1.75zM11.75 3a.75.75 0 00-.75.75v7.5a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75zm-8.25.75a.75.75 0 011.5 0v5.5a.75.75 0 01-1.5 0v-5.5zM8 3a.75.75 0 00-.75.75v3.5a.75.75 0 001.5 0v-3.5A.75.75 0 008 3z"/>
            </svg>
            找回密码
          </h2>
          <button onClick={() => setShowForgotPassword(false)} style={styles.closeButton}>
            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleForgotPassword}>
          <p style={{color: 'var(--color-text-secondary)', marginBottom: '20px', fontSize: '14px'}}>
            请输入您注册时使用的邮箱地址，我们将发送重置链接到您的邮箱。
            <strong>请注意：您需要通过邮件中的链接来完成密码重置。</strong>
          </p>
          
          {loginError && (
            <div style={styles.errorAlert}>
              <svg style={styles.errorIcon} viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M8.22 1.754a.25.25 0 00-.44 0L1.698 13.132a.25.25 0 00.22.368h12.164a.25.25 0 00.22-.368L8.22 1.754zm-1.763-.707c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0114.082 15H1.918a1.75 1.75 0 01-1.543-2.575L6.457 1.047zM9 11a1 1 0 11-2 0 1 1 0 012 0zm-.25-5.25a.75.75 0 00-1.5 0v2.5a.75.75 0 001.5 0v-2.5z"/>
              </svg>
              {loginError}
            </div>
          )}
          
          <div style={styles.formGroup}>
            <label htmlFor="forgotEmail" style={styles.label}>
              邮箱
            </label>
            <input
              id="forgotEmail"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="请输入注册邮箱"
              required
              style={styles.input}
            />
          </div>
          
          <button
            type="submit"
            className="btn btn-primary"
            style={styles.submitButton}
            disabled={loading}
          >
            {loading ? '发送中...' : '发送重置链接'}
          </button>
        </form>

        <div style={styles.footerText}>
          <span 
            style={styles.footerLink}
            onClick={() => setShowForgotPassword(false)}
          >
            返回登录
          </span>
        </div>
      </div>
    );
  }

  // 正常登录/注册界面
  return (
    <div style={styles.modalContainer}>
      <div style={styles.modalHeader}>
        <h2 style={styles.modalTitle}>
          <MemoLogo size={32} style={{ marginRight: '8px' }} />
          {isRegister ? '创建账户' : '登录 MemoHub'}
        </h2>
        <button onClick={onClose} style={styles.closeButton}>
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
          </svg>
        </button>
      </div>

      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button 
            key={tab.id}
            style={{
              ...styles.tab,
              ...(tab.active ? styles.tabActive : styles.tabInactive)
            }}
            onClick={tab.onClick}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(isRegister ? registerError : loginError) && (
        <div style={styles.errorAlert}>
          <svg style={styles.errorIcon} viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M8.22 1.754a.25.25 0 00-.44 0L1.698 13.132a.25.25 0 00.22.368h12.164a.25.25 0 00.22-.368L8.22 1.754zm-1.763-.707c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0114.082 15H1.918a1.75 1.75 0 01-1.543-2.575L6.457 1.047zM9 11a1 1 0 11-2 0 1 1 0 012 0zm-.25-5.25a.75.75 0 00-1.5 0v2.5a.75.75 0 001.5 0v-2.5z"/>
          </svg>
          {isRegister ? registerError : loginError}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label htmlFor="username" style={styles.label}>
            用户名
          </label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={isRegister ? "选择一个用户名" : "请输入用户名"}
            required
            style={styles.input}
            autoFocus
          />
        </div>

        {isRegister && (
          <>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                邮箱
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入邮箱（用于验证）"
                required
                style={styles.input}
                disabled={codeSent}
              />
              <small style={styles.inputHint}>
                用于验证账户和找回密码
              </small>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="verificationCode" style={styles.label}>
                验证码
              </label>
              <div style={styles.codeInputGroup}>
                <input
                  id="verificationCode"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="请输入6位验证码"
                  required
                  maxLength="6"
                  style={{...styles.input, ...styles.codeInput}}
                />
                <button
                  type="button"
                  onClick={sendVerificationCode}
                  className="btn btn-secondary"
                  disabled={codeSent || loading || countdown > 0}
                  style={styles.sendCodeButton}
                >
                  {countdown > 0 ? `${countdown}秒后重试` : '发送验证码'}
                </button>
              </div>
              <small style={styles.inputHint}>
                验证码将发送到您的邮箱，10分钟内有效
              </small>
            </div>
          </>
        )}
        
        <div style={styles.formGroup}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
            <label htmlFor="password" style={styles.label}>
              密码
            </label>
            {!isRegister && (
              <span 
                style={{fontSize: '12px', color: 'var(--color-accent)', textDecoration: 'none', cursor: 'pointer'}}
                onClick={() => setShowForgotPassword(true)}
              >
                忘记密码？
              </span>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              id="password"
              type={isRegister ? (showRegisterPassword ? "text" : "password") : (showLoginPassword ? "text" : "password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? "创建安全密码" : "请输入密码"}
              required
              style={{ ...styles.input, paddingRight: '40px' }}
            />
            <span
              onClick={() => isRegister ? setShowRegisterPassword(!showRegisterPassword) : setShowLoginPassword(!showLoginPassword)}
              style={styles.passwordToggle}
            >
              {isRegister ? (showRegisterPassword ? "🙈" : "👁️") : (showLoginPassword ? "🙈" : "👁️")}
            </span>
          </div>
          {isRegister && (
            <small style={styles.inputHint}>
              至少6个字符
            </small>
          )}
        </div>

        {isRegister && (
          <div style={styles.formGroup}>
            <label htmlFor="confirmPassword" style={styles.label}>
              确认密码
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="confirmPassword"
                type={showRegisterConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="再次输入密码"
                required
                style={{ ...styles.input, paddingRight: '40px' }}
              />
              <span
                onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                style={styles.passwordToggle}
              >
                {showRegisterConfirmPassword ? "🙈" : "👁️"}
              </span>
            </div>
          </div>
        )}
        {isRegister && (
            <div style={{marginBottom: '20px', fontSize: '12px', color: 'var(--color-text-secondary)'}}>
                <label style={{display: 'flex', alignItems: 'center'}}>
                <input 
                    type="checkbox" 
                    required 
                    style={{marginRight: '8px'}}
                />
                <span>
                    我同意{' '}
                    <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{color: 'var(--color-accent)', textDecoration: 'none'}}
                    onClick={(e) => {
                        e.preventDefault();
                        window.open('/terms', '_blank');
                    }}
                    >
                    服务条款
                    </a>
                    {' 和 '}
                    <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{color: 'var(--color-accent)', textDecoration: 'none'}}
                    onClick={(e) => {
                        e.preventDefault();
                        window.open('/privacy', '_blank');
                    }}
                    >
                    隐私政策
                    </a>
                </span>
                </label>
            </div>
        )}
        
        <button
          type="submit"
          className={`btn ${isRegister ? 'btn-success' : 'btn-primary'}`}
          style={styles.submitButton}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner" style={{marginRight: '8px'}}></span>
              {isRegister ? '注册中...' : '登录中...'}
            </>
          ) : isRegister ? '创建账户' : '登录'}
        </button>
      </form>

      <div style={styles.footerText}>
        {isRegister ? '已有账户？' : '还没有账户？'}{' '}
        <span 
          style={styles.footerLink}
          onClick={() => setIsRegister(!isRegister)}
        >
          {isRegister ? '立即登录' : '创建新账户'}
        </span>
      </div>
    </div>
  );
}

export default LoginModal;